import log from '@/lib/logger';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {useMembersTranslation, useCommonTranslation} from '@/i18n/hooks';
import {Dialog, DialogActions, DialogBody, DialogTitle} from '@/components/tailwind/dialog';
import {Button} from '@/components/tailwind/button';
import {Input} from '@/components/tailwind/input';
import ValidationAlert from "@/components/validation-alert.tsx";
import {Field, Label} from "@/components/tailwind/fieldset.tsx";
import {createOneTimeDonation, updateOneTimeDonation} from "@/services/donation-services.ts";
import {useAuth} from "@/context/use-auth.tsx";
import {useEffect, useMemo} from "react";
import {OneTimeDonation} from "@/models/donation.ts";

interface OneTimeDonationDialogProps {
    isOpen: boolean;
    mode: 'add' | 'edit';
    memberId: string;
    donation?: OneTimeDonation;
    onSave: (donation: OneTimeDonation) => void;
    onClose: () => void;
}

const oneTimeDonationSchema = z.object({
    amount: z.string()
        .min(1, 'donationDialogs.oneTime.errors.amount-required')
        .refine((value) => {
            const num = parseFloat(value.replace(',', '.'));
            return !isNaN(num) && num > 0;
        }, 'donationDialogs.oneTime.errors.amount-positive'),

    donationDate: z.string()
        .min(1, 'donationDialogs.oneTime.errors.donationDate-required')
        .refine((value) => {
            const date = new Date(value);
            return !isNaN(date.getTime());
        }, 'donationDialogs.oneTime.errors.donationDate-invalid')
});

export type OneTimeDonationFormData = z.infer<typeof oneTimeDonationSchema>;

export default function OneTimeDonationDialog({
    isOpen,
    mode,
    memberId,
    donation,
    onSave,
    onClose,
}: OneTimeDonationDialogProps) {
    const {t} = useMembersTranslation();
    const {t: tCommon} = useCommonTranslation();
    const {token} = useAuth();

    const defaultValues = useMemo(() => ({
        amount: mode === 'edit' && donation ? donation.amount.toFixed(2).replace('.', ',') : '',
        donationDate: mode === 'edit' && donation ? donation.donationDate : new Date().toISOString().split('T')[0]
    }), [mode, donation]);

    const {
        register,
        handleSubmit,
        control,
        setError,
        formState: {errors},
        reset
    } = useForm<OneTimeDonationFormData>({
        resolver: zodResolver(oneTimeDonationSchema),
        defaultValues
    });

    useEffect(() => {
        if (isOpen) {
            reset(defaultValues);
        }
    }, [isOpen, reset, defaultValues]);

    async function saveData(data: OneTimeDonationFormData): Promise<boolean> {
        const amountValue = parseFloat(data.amount.replace(',', '.'));

        try {
            let response: OneTimeDonation | void;
            if (mode === 'add') {
                response = await createOneTimeDonation(token, memberId, {
                    amount: amountValue,
                    donationDate: data.donationDate
                });
            } else {
                response = await updateOneTimeDonation(token, memberId, donation!.id, {
                    amount: amountValue,
                    donationDate: data.donationDate
                });
            }
            if (response) {
                onSave(response);
            }
            return true;
        } catch (error: unknown) {
            log.error("Error saving one-time donation: ", error);
            const err = error as { status?: number; error?: string; message?: string };
            if (err.status) {
                setError("root", {
                    message: tCommon("serverError.response-code-with-details", {
                        statusCode: err.status,
                        errorDetails: err.error
                    })
                });
            } else {
                setError("root", {message: tCommon("serverError.unknown-with-details", {errorDetails: err.message})});
            }
            return false;
        }
    }

    async function onSubmit(data: OneTimeDonationFormData) {
        if (await saveData(data)) {
            handleClosing();
        }
    }

    function handleClosing() {
        reset();
        onClose();
    }

    const dialogTitle = mode === 'add'
        ? t('donationDialogs.oneTime.titleAdd')
        : t('donationDialogs.oneTime.titleEdit');

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {dialogTitle}
            </DialogTitle>

            <DialogBody>
                {errors.root && (
                    <ValidationAlert message={errors.root?.message ||
                        t('donationDialogs.oneTime.errors.unknown-server-issue')}/>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Field>
                            <Label>
                                {t('donationDialogs.oneTime.fields.amount.label')}
                            </Label>
                            <Input
                                type="text"
                                inputMode="decimal"
                                id="amount"
                                {...register('amount')}
                                invalid={Boolean(errors.amount)}
                                aria-label={t('donationDialogs.oneTime.fields.amount.ariaLabel')}
                                data-testid="one-time-donation-amount"
                            />
                            {errors.amount && (
                                <ValidationAlert
                                    message={t(errors.amount?.message ||
                                        "donationDialogs.oneTime.errors.unknown-amount-issue")}
                                />
                            )}
                        </Field>
                    </div>

                    <div>
                        <Field>
                            <Label>
                                {t('donationDialogs.oneTime.fields.donationDate.label')}
                            </Label>

                            <Controller
                                name="donationDate"
                                control={control}
                                render={({field}) => (
                                    <Input
                                        type="date"
                                        value={field.value || ''}
                                        max={new Date().toISOString().split('T')[0]}
                                        onChange={(e) => {
                                            field.onChange(e.target.value || '');
                                        }}
                                        aria-label={t('donationDialogs.oneTime.fields.donationDate.ariaLabel')}
                                        invalid={Boolean(errors.donationDate)}
                                        data-testid="one-time-donation-date"
                                    />
                                )}
                            />
                            {errors.donationDate && (
                                <ValidationAlert
                                    message={t(errors.donationDate?.message ||
                                        "donationDialogs.oneTime.errors.unknown-donationDate-issue")}
                                />
                            )}
                        </Field>
                    </div>

                    <DialogActions>
                        <Button
                            type="button"
                            plain
                            onClick={handleClosing}
                            aria-label={tCommon('buttons.cancel.ariaLabel')}
                        >
                            {tCommon('buttons.cancel.text')}
                        </Button>
                        <Button
                            type="submit"
                            aria-label={tCommon('buttons.submit.ariaLabel')}
                            data-testid="submit-one-time-donation"
                        >
                            {tCommon('buttons.submit.text')}
                        </Button>
                    </DialogActions>
                </form>
            </DialogBody>
        </Dialog>
    );
}
